import { readFileSync } from 'fs';

import { ArrowBack } from '@mui/icons-material';
import { Box, Button, Container, Grid, Typography } from '@mui/material';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

// Import layout
import ErrorBoundary from '../../../../../components/common/ErrorBoundary';
import LayoutRouter from '../../../../../components/layout/LayoutRouter';
import AddressWarningDialog from '../../../../../components/marketplace/AddressWarningDialog';
import RelatedServices from '../../../../../components/marketplace/RelatedServices';
import ServiceJsonLd from '../../../../../components/seo/ServiceJsonLd';
import BrandDisplay from '../../../../../components/service/BrandDisplay';
import GuidedModePanel from '../../../../../components/service/GuidedModePanel';
import OrderSchemaPanel from '../../../../../components/service/OrderSchemaPanel';
import ServiceBuyBox from '../../../../../components/service/ServiceBuyBox';
import ServiceConfiguration from '../../../../../components/service/ServiceConfiguration';
import ServiceHeader from '../../../../../components/service/ServiceHeader';
import ServiceOptionSections from '../../../../../components/service/ServiceOptionSections';
import type {
  OneTimeOptionSelection,
  RecurringOptionSelection,
} from '../../../../../components/service/ServiceOptionSections';
import ServiceVideoSection from '../../../../../components/service/ServiceVideoSection';
import { WATERMARK_OPACITY } from '../../../../../constants/service';
import { useAddressValidation } from '../../../../../hooks/useAddressValidation';
import { useServiceData } from '../../../../../hooks/useServiceData';
import { useServicePricing } from '../../../../../hooks/useServicePricing';
import { useTermSelection } from '../../../../../hooks/useTermSelection';
import { getRecurringBoltOnsTotalCents } from '../../../../../lib/services/configuredAddons';
import { GuidedModeRecommendation, isMultiLocationGuidedQuoteService } from '../../../../../lib/services/guidedMode';
import type { MarketplaceService } from '../../../../../types/pricing';
import { validateServiceId } from '../../../../../utils/validation';

// =============================================================================
// SSR HELPER FUNCTIONS - Internal API URL resolution
// =============================================================================
// These functions ensure SSR uses internal 127.0.0.1 addresses to bypass
// external routing through Apache reverse proxy.
// Priority: Hostname detection (dev) > .active_env file (prod blue/green)

const ACTIVE_ENV_FILE = '/home/aithonone/public_html/.active_env';

function getActiveEnvironment(): string {
  try {
    return readFileSync(ACTIVE_ENV_FILE, 'utf8').trim();
  } catch {
    return 'unknown';
  }
}

/**
 * Get the API port based on deployment environment
 * Priority: Hostname detection (dev) > .active_env file (prod blue/green)
 */
function getApiPortFromHostname(hostname: string): string {
  const host = (hostname || '').toLowerCase();

  // First: Check if this is dev by hostname (visionarynow.com)
  if (host.includes('visionarynow.com')) {
    return '3010';
  }

  // Second: Check .active_env file for production blue/green
  const activeEnv = getActiveEnvironment();
  if (activeEnv === 'green') return '3030';
  if (activeEnv === 'blue') return '3020';

  // Fallback: Default to production blue
  return '3020';
}

function getInternalApiUrl(path: string, hostname?: string): string {
  const port = hostname ? getApiPortFromHostname(hostname) : '3010';
  return `http://127.0.0.1:${port}${path}`;
}

// =============================================================================

interface CatalogServiceDetailPageProps {
  serviceId: string;
  catalogInstance: string;
  catalogData?: any; // Catalog instance data
  serviceData?: MarketplaceService;
  initialService?: MarketplaceService; // Keep for backward compatibility
  error?: string;
}

/**
 * FAANG-GRADE: Catalog Service Detail Page
 * Displays service details within a catalog context
 * Maintains catalog branding and navigation
 */
export default function CatalogServiceDetailPage({
  serviceId,
  catalogInstance,
  catalogData: initialCatalogData,
  serviceData,
  initialService,
  error: serverError,
}: CatalogServiceDetailPageProps) {
  const router = useRouter();

  // State for catalog data
  const [catalogData, setCatalogData] = useState<any>(initialCatalogData);
  const [catalogLoading, setCatalogLoading] = useState(!initialCatalogData);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  // State for service selection (matching main catalog)
  const [selectedVariant, setSelectedVariant] = useState<string | undefined>();
  const [selectedFrequency, setSelectedFrequency] = useState<string | undefined>();
  const [guidedRecommendation, setGuidedRecommendation] = useState<GuidedModeRecommendation | null>(null);
  const [recurringOptionSelections, setRecurringOptionSelections] = useState<RecurringOptionSelection[]>([]);
  const [oneTimeOptionSelections, setOneTimeOptionSelections] = useState<OneTimeOptionSelection[]>([]);

  // Fetch catalog data if not provided
  useEffect(() => {
    if (!initialCatalogData && catalogInstance) {
      const fetchCatalog = async () => {
        try {
          setCatalogLoading(true);
          const response = await fetch(`/api/catalog-context/${catalogInstance}`);
          if (!response.ok) {
            throw new Error(`Failed to fetch catalog: ${response.status}`);
          }
          const data = await response.json();
          setCatalogData(data.catalog);
        } catch (error) {
          console.error('Catalog fetch error:', error);
          setCatalogError('Failed to load catalog information');
        } finally {
          setCatalogLoading(false);
        }
      };
      fetchCatalog();
    }
  }, [catalogInstance, initialCatalogData]);

  // Use serviceData as initialService if available (for SSR compatibility)
  const effectiveInitialService = serviceData || initialService;

  // Use custom hooks
  const { service, loading, error, refetch } = useServiceData(
    serviceId,
    effectiveInitialService,
    effectiveInitialService?.default_term_months,
    catalogInstance,
  );
  const { selectedTerm, handleTermChange } = useTermSelection(service);
  const { selectedVariantPrice } = useServicePricing(
    service,
    selectedVariant,
    selectedFrequency,
    selectedTerm,
  );
  const { addressWarningDialog, handleAddressWarningConfirm, handleAddressWarningCancel } =
    useAddressValidation(service, router);
  const isMultiLocationGuidedQuote = service ? isMultiLocationGuidedQuoteService(service) : false;

  // Refetch service data when term changes
  const handleTermChangeWithRefetch = (term: number) => {
    handleTermChange(term);
    setSelectedVariant(undefined); // Reset variant selection when term changes
    refetch(term); // Refetch with new term for term-specific pricing
  };

  // Auto-select default variant if available
  useEffect(() => {
    if (service && !selectedVariant && service.variants?.variants) {
      const defaultVariant = service.variants.variants.find((v: any) => v.is_default); // variants-access-ok (gated by `service.variants?.variants` above)
      if (defaultVariant) {
        setSelectedVariant(defaultVariant.key);
      }
    }
  }, [service, selectedVariant]);

  // Catalog-aware back navigation
  const handleGoBack = () => {
    router.push(`/c/${catalogInstance}`);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>Loading service details...</Typography>
      </Container>
    );
  }

  if (error || !service) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography color="error">{error || serverError || 'Service not found'}</Typography>
        <Button startIcon={<ArrowBack />} onClick={handleGoBack} sx={{ mt: 2 }}>
          Back to Catalog
        </Button>
      </Container>
    );
  }

  const variantKey = selectedVariant || service.default_variant_key;
  const monthlyOptionsTotalCents = getRecurringBoltOnsTotalCents(
    service,
    variantKey,
    recurringOptionSelections,
  );
  const selectedMonthlyTotalCents = selectedVariantPrice === undefined
    ? undefined
    : selectedVariantPrice + monthlyOptionsTotalCents;

  return (
    <LayoutRouter
      catalogBranding={
        catalogData
          ? {
              name: catalogData.name,
              logo: catalogData.branding?.logo,
              primaryColor: catalogData.branding?.primaryColor,
              website: catalogData.branding?.website,
            }
          : undefined
      }
    >
      <Head>
        <title>{service?.name ? `${service.name} - Service Details` : 'Service Details'}</title>
        {service?.description && <meta name="description" content={service.description} />}
        <link rel="canonical" href={`https://aithon.tech/c/${catalogInstance}/services/${serviceId}`} />
      </Head>
      {service && (
        <ServiceJsonLd
          name={service.name}
          description={service.description || ''}
          url={`https://aithon.tech/c/${catalogInstance}/services/${serviceId}`}
          priceCents={(service as any).base_price || (service as any).price || 0}
          pricingModel={(service as any).pricing_model}
          category={service.category}
          brandName={(service as any).brand_name}
          providerName={catalogData?.name || 'Aithon'}
          lowPriceCents={service.variants?.variants?.length
            ? Math.min(...service.variants.variants.map((v: any) => v.price || 0).filter((p: number) => p > 0)) // variants-access-ok (optional-chain length guard)
            : undefined}
          highPriceCents={service.variants?.variants?.length
            ? Math.max(...service.variants.variants.map((v: any) => v.price || 0)) // variants-access-ok (optional-chain length guard)
            : undefined}
          offerCount={service.variants?.variants?.length || 1}
        />
      )}
            <ErrorBoundary>
      {/* SSR breadcrumb — visible to crawlers */}
      {serviceData && (
        <Box component="nav" aria-label="breadcrumb" sx={{ bgcolor: "background.paper", borderBottom: 1, borderColor: "divider", py: 1, px: { xs: 2, md: 4 } }}>
          <Box sx={{ maxWidth: 1200, mx: "auto", display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
            <Box component="a" href={"/" + "c/" + catalogInstance} sx={{ fontSize: "0.75rem", color: "text.secondary", textDecoration: "none", "&:hover": { color: "primary.main" } }}>{catalogData?.name || catalogInstance}</Box>
            {(serviceData as any).category && <>
              <Typography variant="caption" color="text.disabled">/</Typography>
              <Box component="a" href={"/c/" + catalogInstance + "?category=" + encodeURIComponent((serviceData as any).category)} sx={{ fontSize: "0.75rem", color: "text.secondary", textDecoration: "none", "&:hover": { color: "primary.main" } }}>{(serviceData as any).category}</Box>
            </>}
            <Typography variant="caption" color="text.disabled">/</Typography>
            <Typography variant="caption" color="text.primary" fontWeight={600}>{serviceData.name}</Typography>
            {(serviceData as any).brand_name && (
              <Typography variant="caption" color="text.disabled" sx={{ ml: "auto" }}>by {(serviceData as any).brand_name}</Typography>
            )}
          </Box>
        </Box>
      )}


        {/* Full-screen watermark background */}
        {service.brand_logo_path && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundImage: `url(${service.brand_logo_path})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              opacity: WATERMARK_OPACITY,
              pointerEvents: 'none',
              zIndex: 0,
            }}
          />
        )}

        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Catalog-aware back button */}
          <Button
            startIcon={<ArrowBack />}
            onClick={handleGoBack}
            sx={{ mb: 2 }}
            variant="outlined"
          >
            Back to Catalog
          </Button>

          <Grid container spacing={4}>
            {/* Main Content (2/3 width) */}
            <Grid item xs={12} md={8}>
              <ServiceHeader service={service} />

              {isMultiLocationGuidedQuote ? (
                <GuidedModePanel service={service as any} onRecommendationChange={setGuidedRecommendation} />
              ) : (
                <ServiceConfiguration
                  service={service}
                  selectedVariant={selectedVariant}
                  selectedFrequency={selectedFrequency}
                  onVariantChange={setSelectedVariant}
                  onFrequencyChange={setSelectedFrequency}
                  selectedTerm={selectedTerm}
                  selectedMonthlyTotalCents={selectedMonthlyTotalCents}
                  monthlyOptionsTotalCents={monthlyOptionsTotalCents}
                  onTermChange={handleTermChangeWithRefetch}
                />
              )}

              <ServiceOptionSections
                service={service}
                selectedVariant={selectedVariant}
                recurringSelections={recurringOptionSelections}
                onRecurringSelectionsChange={setRecurringOptionSelections}
                oneTimeSelections={oneTimeOptionSelections}
                onOneTimeSelectionsChange={setOneTimeOptionSelections}
              />

              <OrderSchemaPanel service={service} />

            </Grid>

            {/* Right Column - Buy Box (1/3 width) */}
            <Grid item xs={12} md={4}>
              <ServiceBuyBox
                service={service}
                selectedVariant={selectedVariant}
                selectedVariantPrice={selectedVariantPrice}
                selectedTerm={selectedTerm}
                selectedFrequency={selectedFrequency}
                guidedRecommendation={guidedRecommendation}
                showGuidedPanel={!isMultiLocationGuidedQuote}
                configuredRecurringSelections={recurringOptionSelections}
                configuredOneTimeSelections={oneTimeOptionSelections}
                showConfiguredOptions={false}
              />
            </Grid>

            {/* Service-specific video — full width, above About the company */}
            {(service as any).service_video_url && (
              <Grid item xs={12}>
                <ServiceVideoSection
                  videoUrl={(service as any).service_video_url}
                  serviceName={service.name}
                />
              </Grid>
            )}

            {/* Provider Information — full width below both columns */}
            <Grid item xs={12}>
              <BrandDisplay
                brandName={service.brand_name}
                brandDescription={service.brand_description}
                brandLogoPath={service.brand_logo_path}
                brandVideoUrl={service.brand_video_url}
                serviceVideoUrl={service.service_video_url}
                brandWebsite={service.brand_website}
                installDays={service.expected_install_time_days}
              />
            </Grid>
          </Grid>

          <AddressWarningDialog
            open={addressWarningDialog.open}
            onClose={handleAddressWarningCancel}
            onConfirm={handleAddressWarningConfirm}
            onCancel={handleAddressWarningCancel}
            serviceName={service.name}
          />

          {/* Customers Also Viewed */}
          <RelatedServices serviceId={serviceId} catalogInstance={catalogInstance as string} />
        </Container>
      </ErrorBoundary>
    </LayoutRouter>
  );
}

export const getServerSideProps: GetServerSideProps<CatalogServiceDetailPageProps> = async (
  context,
) => {
  const { catalogInstance, id } = context.params || {};
  const validatedId = validateServiceId(id);
  const hostname = context.req.headers.host || 'localhost:3011';

  console.log('[SSR-CATALOG-SVC] ============================================');
  console.log('[SSR-CATALOG-SVC] CatalogServiceDetailPage SSR Start');
  console.log('[SSR-CATALOG-SVC] catalogInstance:', catalogInstance);
  console.log('[SSR-CATALOG-SVC] serviceId:', id);
  console.log('[SSR-CATALOG-SVC] validatedId:', validatedId);
  console.log('[SSR-CATALOG-SVC] hostname:', hostname);
  console.log('[SSR-CATALOG-SVC] ============================================');

  if (!validatedId || !catalogInstance) {
    console.log('[SSR-CATALOG-SVC] Missing required params, returning 404');
    return {
      notFound: true,
    };
  }

  try {
    // Use internal API URL based on hostname (dev vs prod detection)
    const catalogApiUrl = getInternalApiUrl(`/api/catalog-context/${catalogInstance}`, hostname);
    console.log('[SSR-CATALOG-SVC] Fetching catalog from:', catalogApiUrl);

    const catalogResponse = await fetch(catalogApiUrl);
    console.log('[SSR-CATALOG-SVC] Catalog API response status:', catalogResponse.status);

    let catalogData = null;
    if (catalogResponse.ok) {
      const catalogResult = await catalogResponse.json();
      catalogData = catalogResult.catalog;
      console.log('[SSR-CATALOG-SVC] Catalog data fetched successfully');
    } else {
      console.log('[SSR-CATALOG-SVC] Catalog fetch failed, continuing without catalog data');
    }

    // Fetch service data
    const serviceApiUrl = getInternalApiUrl(`/api/catalog-context/${catalogInstance}/services/${validatedId}`, hostname);
    console.log('[SSR-CATALOG-SVC] Fetching service from:', serviceApiUrl);

    const serviceResponse = await fetch(serviceApiUrl);
    console.log('[SSR-CATALOG-SVC] Service API response status:', serviceResponse.status);

    if (!serviceResponse.ok) {
      if (serviceResponse.status === 404) {
        console.log('[SSR-CATALOG-SVC] Service not found (404), returning 404');
        return {
          notFound: true,
        };
      }
      const errorText = await serviceResponse.text();
      console.log('[SSR-CATALOG-SVC] Service API error:', errorText);
      throw new Error(`Failed to fetch service: ${serviceResponse.status}`);
    }

    const serviceData = await serviceResponse.json();
    console.log('[SSR-CATALOG-SVC] Service data fetched successfully');

    // If service has a default term, refetch with the default term for proper SSR pricing
    const service = serviceData.service || serviceData;
    if (service.default_term_months) {
      console.log('[SSR-CATALOG-SVC] Refetching with default term:', service.default_term_months);
      const serviceWithDefaultTermUrl = getInternalApiUrl(
        `/api/catalog-context/${catalogInstance}/services/${validatedId}?term=${service.default_term_months}`,
      );
      const defaultTermResponse = await fetch(serviceWithDefaultTermUrl);

      if (defaultTermResponse.ok) {
        const defaultTermData = await defaultTermResponse.json();
        console.log('[SSR-CATALOG-SVC] Successfully fetched data with default term');

        console.log('[SSR-CATALOG-SVC] ============================================');
        console.log('[SSR-CATALOG-SVC] CatalogServiceDetailPage SSR Complete');
        console.log('[SSR-CATALOG-SVC] ============================================');

        return {
          props: {
            serviceId: validatedId,
            catalogInstance: catalogInstance as string,
            catalogData,
            serviceData: defaultTermData.service || defaultTermData,
          },
        };
      } else {
        console.log('[SSR-CATALOG-SVC] Failed to fetch with default term, using base data');
      }
    }

    console.log('[SSR-CATALOG-SVC] ============================================');
    console.log('[SSR-CATALOG-SVC] CatalogServiceDetailPage SSR Complete');
    console.log('[SSR-CATALOG-SVC] ============================================');

    return {
      props: {
        serviceId: validatedId,
        catalogInstance: catalogInstance as string,
        catalogData,
        serviceData: service,
      },
    };
  } catch (error) {
    console.error('[SSR-CATALOG-SVC] Error:', error);
    return {
      props: {
        serviceId: validatedId,
        catalogInstance: catalogInstance as string,
        error: 'Failed to load service details',
      },
    };
  }
};
